/**
 * Public service health page.
 * Reads `/api/public/status` and renders sub-system cards + incidents.
 *
 * Auto-refreshes every 60s. Designed to be the URL you'd point an
 * uptime monitor at and to deep-link from /trust.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Activity, CheckCircle, AlertTriangle, AlertOctagon, RefreshCw, ShieldCheck,
  Zap, Lock, Globe, Plug, Rss, Mail, Loader2,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
// Phase 14.7 — Hostname-aware brand chrome. On `status.qorbom.com`
// we swap the QorTrace nav/footer for QorbomNav + a QorBOM-styled
// footer so visitors arriving via the QorBOM subdomain see the
// QorBOM brand experience throughout.
import QorbomNav from "../components/QorbomNav";
import QorbomBrand from "../components/qorbom/QorbomBrand";
import useDocumentTitle from "../hooks/useDocumentTitle";
import usePageMeta from "../hooks/usePageMeta";
import M from "../lib/pageMetaConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


// Phase 14.7 — Detect whether the SPA is being viewed under the
// `status.qorbom.com` hostname. Used to swap brand chrome AND
// product-name tokens in headings so "QorTrace systems are…" reads
// "QorBOM Partner API is…" for partner-facing visitors.
function _isQorbomStatusHost() {
  if (typeof window === "undefined") return false;
  return (window.location.hostname || "").toLowerCase() === "status.qorbom.com";
}

const STATUS_META = {
  operational: { color: "#1FDD83", icon: CheckCircle,    label: "Operational" },
  degraded:    { color: "#FFD60A", icon: AlertTriangle,  label: "Degraded"    },
  outage:      { color: "#FF6E6E", icon: AlertOctagon,   label: "Outage"      },
  unknown:     { color: "#9aa5b8", icon: AlertTriangle,  label: "Unknown"     },
};

export default function Status() {
  usePageMeta(M.status);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err,   setErr]       = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await axios.get(`${API}/public/status`, { timeout: 8000 });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "fetch failed");
    } finally {
      setLoading(false);
    }
  }, []);

  // Iter269.17 — Uptime history. Loaded once on mount + refreshed
  // every 5 minutes (matches the slash-health monitor's tick). Never
  // blocks the main /status payload — fails open with `null`.
  const [uptime, setUptime] = useState(null);
  const refreshUptime = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/public/status/uptime`,
                                { timeout: 8000 });
      setUptime(r.data);
    } catch {
      setUptime(null);
    }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 60_000);
    return () => clearInterval(t);
  }, [refresh]);

  useEffect(() => {
    refreshUptime();
    const t = setInterval(refreshUptime, 5 * 60_000);
    return () => clearInterval(t);
  }, [refreshUptime]);

  const overall = data?.overall || "unknown";
  const meta    = STATUS_META[overall] || STATUS_META.unknown;
  const Icon    = meta.icon;

  // Phase 14.7 — hostname check captured once per render so the
  // chrome + product-name swaps stay in sync.
  const qbHost = _isQorbomStatusHost();
  const productName = qbHost ? "QorBOM" : "QorTrace";

  return (
    <div className="min-h-screen qt-radial-bg">
      {qbHost ? <QorbomNav active="status" /> : <Header />}
      <div className="max-w-[1100px] mx-auto px-6 sm:px-8 py-12"
           data-testid="status-page">

        <div className="flex items-center gap-2 mb-3">
          <Activity size={14} style={{ color: "var(--qt-cyan)" }} />
          <div className="qt-label" style={{ letterSpacing: "0.18em" }}>
            SERVICE STATUS
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
          <div>
            <h1 className="qt-h2 text-3xl sm:text-4xl"
                data-testid="status-title">
              {productName} systems are{" "}
              <span style={{ color: meta.color }}>
                {meta.label.toLowerCase()}
              </span>.
            </h1>
            <p className="mt-3 max-w-2xl"
               style={{ color: "var(--qt-text-2)", lineHeight: 1.7 }}>
              Auto-refreshes every 60 seconds. Bookmark this page or
              point any uptime monitor at{" "}
              <code className="qt-mono text-[12px]"
                    style={{ color: "var(--qt-cyan)" }}>
                /api/public/status
              </code>.
            </p>
          </div>
          <button onClick={refresh}
                  data-testid="status-refresh"
                  className="qt-btn qt-btn-ghost inline-flex items-center gap-2"
                  style={{ fontSize: 12, padding: "8px 14px" }}>
            <RefreshCw size={12}
                       style={{ animation: loading ? "qt-spin 1s linear infinite" : "none" }} />
            Refresh
          </button>
        </div>

        {/* Big overall pill */}
        <div data-testid="status-overall"
             style={{ marginTop: 18, padding: "20px 24px", borderRadius: 8,
                      background: `linear-gradient(180deg, ${meta.color}15, transparent)`,
                      border: `1px solid ${meta.color}55`,
                      display: "flex", alignItems: "center", gap: 16 }}>
          <Icon size={32} style={{ color: meta.color, flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div className="qt-mono"
                 style={{ color: meta.color, fontWeight: 800,
                          letterSpacing: "0.16em", fontSize: 14 }}>
              {meta.label.toUpperCase()}
            </div>
            <div style={{ color: "var(--qt-text-2)", fontSize: 13, marginTop: 4 }}>
              {data?.checked_at
                ? `Last checked ${new Date(data.checked_at).toLocaleTimeString()}`
                : "Checking…"}
              {data?.uptime_pct_30d != null && (
                <span style={{ marginLeft: 14 }}>
                  · 30-day uptime{" "}
                  <span style={{ color: meta.color, fontWeight: 700 }}>
                    {data.uptime_pct_30d.toFixed(2)}%
                  </span>
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Live API latency strip — single inline card */}
        {data?.api_latency && (
          <LatencyStrip latency={data.api_latency} />
        )}

        {/* Subsystem grid */}
        <div style={{ marginTop: 22, display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                      gap: 12 }}>
          {(data?.subsystems || []).map((s) => {
            const m = STATUS_META[s.status] || STATUS_META.unknown;
            const SI = m.icon;
            return (
              <div key={s.key}
                   data-testid={`status-sub-${s.key}`}
                   style={{ padding: 16, borderRadius: 6,
                            background: "rgba(4,9,20,0.55)",
                            border: `1px solid ${m.color}40` }}>
                <div style={{ display: "flex", alignItems: "center",
                              gap: 8, marginBottom: 8 }}>
                  <SI size={16} style={{ color: m.color }} />
                  <span className="qt-mono"
                        style={{ color: m.color, fontWeight: 700,
                                 letterSpacing: "0.14em", fontSize: 11 }}>
                    {m.label.toUpperCase()}
                  </span>
                </div>
                <div style={{ color: "#fff", fontWeight: 600, fontSize: 15 }}>
                  {s.name}
                </div>
                <div style={{ color: "var(--qt-text-3)", fontSize: 12,
                              marginTop: 6, lineHeight: 1.5 }}>
                  {s.detail}
                </div>
                {/* Iter269.16 — per-card freshness stamp. Mirrors the
                    top-level "Last checked HH:MM:SS" so visitors can
                    confirm every subsystem was just polled. */}
                {s.last_checked && (
                  <div className="qt-mono"
                       data-testid={`status-sub-${s.key}-last-checked`}
                       style={{ color: "var(--qt-text-3)", fontSize: 10,
                                letterSpacing: "0.08em", marginTop: 8,
                                opacity: 0.7 }}>
                    LAST CHECKED · {new Date(s.last_checked).toLocaleTimeString()}
                  </div>
                )}
                {/* Iter269.17 — 24h uptime sparkline. Renders only for
                    subsystems with probe data; others omit it. */}
                <UptimeSparkline subsystemKey={s.key} uptime={uptime} />
              </div>
            );
          })}
        </div>

        {/* Third-party provider dependencies */}
        {data?.providers?.length > 0 && (
          <div style={{ marginTop: 32 }} data-testid="status-providers-section">
            <div className="qt-label"
                 style={{ letterSpacing: "0.18em", marginBottom: 10,
                          display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Plug size={11} /> THIRD-PARTY DEPENDENCIES
            </div>
            <p style={{ color: "var(--qt-text-3)", fontSize: 12,
                        lineHeight: 1.5, marginBottom: 10, maxWidth: 640 }}>
              Live health of the upstream services QorTrace relies on. Refreshed every 5 minutes.
            </p>
            <div style={{ display: "grid",
                          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                          gap: 12 }}>
              {data.providers.map((p) => (
                <ProviderCard key={p.key} provider={p} />
              ))}
            </div>
          </div>
        )}

        {/* Cert + DNS health */}
        {data?.cert_dns?.length > 0 && (
          <div style={{ marginTop: 32 }} data-testid="status-cert-dns-section">
            <div className="qt-label"
                 style={{ letterSpacing: "0.18em", marginBottom: 10,
                          display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Lock size={11} /> CERTIFICATE &amp; DNS HEALTH
            </div>
            <p style={{ color: "var(--qt-text-3)", fontSize: 12,
                        lineHeight: 1.5, marginBottom: 10, maxWidth: 640 }}>
              SSL certificates, DNS resolution and HSTS policy per public domain. Refreshed every 30 minutes.
            </p>
            <div style={{ display: "grid",
                          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
                          gap: 12 }}>
              {data.cert_dns.map((c) => (
                <CertDnsCard key={c.host} row={c} />
              ))}
            </div>
          </div>
        )}

        {/* Incidents */}
        <div style={{ marginTop: 32 }}>
          <div className="qt-label"
               style={{ letterSpacing: "0.18em", marginBottom: 10 }}>
            INCIDENTS · 90 DAYS
          </div>
          {data?.incidents?.length ? (
            data.incidents.map((i, idx) => (
              <Incident key={idx} inc={i} />
            ))
          ) : (
            <div data-testid="status-no-incidents"
                 style={{ padding: 18, borderRadius: 6,
                          background: "rgba(123,217,168,0.05)",
                          border: "1px solid rgba(123,217,168,0.20)",
                          color: "var(--qt-text-2)", fontSize: 13,
                          lineHeight: 1.6 }}>
              No incidents reported in the last 90 days.
              All systems running clean.
            </div>
          )}
        </div>

        {err && (
          <div style={{ marginTop: 18, padding: 12, borderRadius: 4,
                        background: "rgba(255,128,0,0.08)",
                        border: "1px solid #FF8000",
                        color: "#FF8000", fontSize: 12 }}>
            {String(err)}
          </div>
        )}

        {/* Iter269.17 — 30-day track record. Same data source as the
            per-card 24h sparkline; rendered as a 90-bucket ribbon at
            the bottom so visitors can see "how reliable is this
            company over time", not just "is it up right now". Only
            subsystems with probe coverage appear. */}
        <ThirtyDayRibbon
          uptime={uptime}
          subsystemMeta={
            (data?.subsystems || []).reduce((acc, s) => {
              acc[s.key] = s.name;
              return acc;
            }, {})
          }
        />

        {/* Subscribe + RSS */}
        <SubscribeRow />

        <div style={{ marginTop: 38, display: "flex", flexWrap: "wrap",
                      gap: 16, color: "var(--qt-text-3)", fontSize: 12,
                      lineHeight: 1.6 }}>
          <Link to="/trust"
                style={{ color: "var(--qt-cyan)",
                         textDecoration: "underline",
                         display: "inline-flex", alignItems: "center", gap: 4 }}>
            <ShieldCheck size={12} /> Security & compliance posture
          </Link>
          <Link to="/methodology"
                style={{ color: "var(--qt-cyan)", textDecoration: "underline" }}>
            Audit methodology
          </Link>
          <Link to="/threat-radar"
                style={{ color: "var(--qt-cyan)", textDecoration: "underline" }}>
            Threat Radar
          </Link>
        </div>
      </div>
      <style>{`@keyframes qt-spin { to { transform: rotate(360deg); } }`}</style>
      {qbHost ? <QorbomStatusFooter /> : <Footer />}
    </div>
  );
}


// Phase 14.7 — Minimal QorBOM-styled footer for status.qorbom.com.
// Doesn't pull in the full marketing footer; just brand + 3 cross-links.
function QorbomStatusFooter() {
  return (
    <footer className="mt-20 border-t border-slate-800 bg-slate-950"
            data-testid="status-qorbom-footer">
      <div className="mx-auto flex max-w-[1100px] flex-wrap items-center
                      justify-between gap-4 px-6 py-8 text-sm text-slate-400">
        <div className="flex items-center gap-2">
          <QorbomBrand className="font-bold text-slate-200"
                       tmClassName="ml-0.5 align-super text-[0.55em] font-semibold text-cyan-400" />
          <span>· Partner API</span>
        </div>
        <div className="flex gap-6">
          <Link to="/qorbom/api-docs" className="hover:text-slate-200">Docs</Link>
          <Link to="/qorbom/become-a-partner" className="hover:text-slate-200">Apply</Link>
          <Link to="/qorbom/trust" className="hover:text-slate-200">Trust</Link>
        </div>
      </div>
    </footer>
  );
}

function Incident({ inc }) {
  const isResolved = inc.status === "resolved";
  const accent = isResolved ? "#1FDD83"
              : inc.status === "investigating" ? "#FFD60A"
              : "#FF6E6E";
  return (
    <div data-testid="status-incident"
         style={{ marginBottom: 10, padding: 16, borderRadius: 6,
                  background: "rgba(4,9,20,0.55)",
                  border: `1px solid ${accent}40` }}>
      <div style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div style={{ flex: 1 }}>
          <div className="qt-mono"
               style={{ color: accent, fontWeight: 700,
                        letterSpacing: "0.14em", fontSize: 11,
                        marginBottom: 4 }}>
            {(inc.status || "open").toUpperCase()}
          </div>
          <div style={{ color: "#fff", fontWeight: 600, fontSize: 15 }}>
            {inc.title || "Incident"}
          </div>
          {inc.summary && (
            <div style={{ color: "var(--qt-text-2)", fontSize: 13,
                          marginTop: 6, lineHeight: 1.55 }}>
              {inc.summary}
            </div>
          )}
        </div>
        <div className="qt-mono"
             style={{ color: "var(--qt-text-3)", fontSize: 11,
                      letterSpacing: "0.06em", whiteSpace: "nowrap" }}>
          {inc.created_at?.slice(0, 16)?.replace("T", " ") || ""}
        </div>
      </div>
    </div>
  );
}


function LatencyStrip({ latency }) {
  const m = STATUS_META[latency.status] || STATUS_META.unknown;
  const ping = latency.db_ping_ms;
  return (
    <div data-testid="status-latency"
         style={{ marginTop: 14, padding: "14px 18px", borderRadius: 6,
                  background: "rgba(4,9,20,0.55)",
                  border: `1px solid ${m.color}40`,
                  display: "flex", alignItems: "center", gap: 14,
                  flexWrap: "wrap" }}>
      <Zap size={16} style={{ color: m.color, flexShrink: 0 }} />
      <div className="qt-mono"
           style={{ color: m.color, fontWeight: 700,
                    letterSpacing: "0.14em", fontSize: 11 }}>
        LIVE API LATENCY
      </div>
      <div style={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>
        {ping != null ? `${ping} ms` : "—"}
      </div>
      <div style={{ color: "var(--qt-text-3)", fontSize: 12 }}>
        {latency.detail} · measured at request time
      </div>
    </div>
  );
}


function ProviderCard({ provider }) {
  const m = STATUS_META[provider.status] || STATUS_META.unknown;
  const SI = m.icon;
  return (
    <div data-testid={`status-provider-${provider.key}`}
         style={{ padding: 16, borderRadius: 6,
                  background: "rgba(4,9,20,0.55)",
                  border: `1px solid ${m.color}40` }}>
      <div style={{ display: "flex", alignItems: "center",
                    gap: 8, marginBottom: 8 }}>
        <SI size={16} style={{ color: m.color }} />
        <span className="qt-mono"
              style={{ color: m.color, fontWeight: 700,
                       letterSpacing: "0.14em", fontSize: 11 }}>
          {m.label.toUpperCase()}
        </span>
      </div>
      <div style={{ color: "#fff", fontWeight: 600, fontSize: 15 }}>
        {provider.name}
        {provider.key === "stripe" && provider.mode && (
          <span data-testid="status-stripe-mode-badge"
                className="qt-mono"
                style={{
                  marginLeft: 8,
                  fontSize: 9, padding: "2px 6px", borderRadius: 3,
                  letterSpacing: "0.16em", verticalAlign: "middle",
                  color: provider.mode === "live" ? "#1FDD83"
                       : provider.mode === "test" ? "var(--qt-amber)"
                       : "#FF6E6E",
                  border: `1px solid ${
                    provider.mode === "live" ? "#1FDD83"
                    : provider.mode === "test" ? "var(--qt-amber)"
                    : "#FF6E6E"}55`,
                  background: `${
                    provider.mode === "live" ? "#1FDD83"
                    : provider.mode === "test" ? "var(--qt-amber)"
                    : "#FF6E6E"}14`,
                }}>
            {provider.mode.toUpperCase()}
            {provider.webhook_signature_verified === false &&
              provider.mode === "live" && " · ⚠ NO WEBHOOK SECRET"}
          </span>
        )}
      </div>
      <div style={{ color: "var(--qt-text-3)", fontSize: 12,
                    marginTop: 6, lineHeight: 1.5 }}>
        {provider.detail}
      </div>
    </div>
  );
}


function CertDnsCard({ row }) {
  const m = STATUS_META[row.status] || STATUS_META.unknown;
  return (
    <div data-testid={`status-cert-dns-${row.host}`}
         style={{ padding: 16, borderRadius: 6,
                  background: "rgba(4,9,20,0.55)",
                  border: `1px solid ${m.color}40` }}>
      <div style={{ display: "flex", alignItems: "baseline",
                    gap: 8, marginBottom: 10 }}>
        <Globe size={14} style={{ color: m.color }} />
        <span style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>
          {row.host}
        </span>
      </div>
      <CertDnsRow label="SSL"   item={row.ssl}  />
      <CertDnsRow label="DNS"   item={row.dns}  />
      <CertDnsRow label="HSTS"  item={row.hsts} />
    </div>
  );
}

function CertDnsRow({ label, item }) {
  const m = STATUS_META[item.status] || STATUS_META.unknown;
  return (
    <div style={{ display: "flex", alignItems: "center",
                  gap: 8, padding: "4px 0",
                  borderTop: "1px solid rgba(255,255,255,0.04)" }}>
      <span className="qt-mono"
            style={{ color: "var(--qt-text-3)",
                     letterSpacing: "0.12em", fontSize: 10,
                     width: 42, flexShrink: 0 }}>
        {label}
      </span>
      <span style={{ display: "inline-block", width: 8, height: 8,
                     borderRadius: 99, background: m.color,
                     flexShrink: 0 }} />
      <span style={{ color: "var(--qt-text-2)", fontSize: 12,
                     lineHeight: 1.4 }}>
        {item.detail}
      </span>
    </div>
  );
}


/* Iter269.17 — Uptime visualisations.
 *
 * UptimeStrip = horizontal row of N small dots. Used both for the
 * per-card 24h sparkline (24 dots) and the page-bottom 30-day ribbon
 * (90 dots). Older buckets sit on the LEFT, newest on the right —
 * matches the convention every other status page in the industry uses.
 *
 * `unknown` buckets render as muted grey so visitors can tell
 * "haven't checked yet" from "checked and found a problem".
 */
function UptimeStrip({ buckets, height = 8, gap = 2, testId }) {
  if (!buckets || buckets.length === 0) return null;
  const colourFor = (status) => {
    if (status === "ok")       return "#1FDD83";
    if (status === "degraded") return "#FFC061";
    if (status === "outage")   return "#FF6E6E";
    return "rgba(255,255,255,0.08)";
  };
  return (
    <div data-testid={testId}
         style={{ display: "flex", alignItems: "center", gap }}>
      {buckets.map((b, i) => (
        <span
          key={i}
          title={`${new Date(b.start).toLocaleString()} → ${b.status}`}
          style={{
            display: "inline-block",
            width: "100%",
            maxWidth: 12,
            flex: "1 1 0",
            height,
            borderRadius: 2,
            background: colourFor(b.status),
            transition: "background-color 200ms ease",
          }}
        />
      ))}
    </div>
  );
}

/* Per-card 24h sparkline. Renders only when we have data for the
 * subsystem; subsystems WITHOUT a probe path (waf, audit_engine,
 * scanner, atlas_treasury) get nothing — we never fabricate history. */
function UptimeSparkline({ subsystemKey, uptime }) {
  const rows = uptime?.window_24h?.subsystems?.[subsystemKey];
  if (!rows) return null;
  const okCount = rows.filter((r) => r.status === "ok").length;
  const knownCount = rows.filter((r) => r.status !== "unknown").length;
  const pct = knownCount > 0 ? Math.round((okCount / knownCount) * 100) : null;
  return (
    <div style={{ marginTop: 10 }}>
      <UptimeStrip
        buckets={rows}
        height={6}
        gap={2}
        testId={`status-sub-${subsystemKey}-sparkline`}
      />
      <div className="qt-mono"
           style={{ color: "var(--qt-text-3)", fontSize: 9,
                    letterSpacing: "0.08em", marginTop: 4, opacity: 0.7,
                    display: "flex", justifyContent: "space-between" }}>
        <span>24H · {pct !== null ? `${pct}% OK` : "BUILDING HISTORY…"}</span>
        <span>NOW →</span>
      </div>
    </div>
  );
}

/* Bottom-of-page 30-day track record. One ribbon per tracked
 * subsystem; subsystems without a probe path are omitted. */
function ThirtyDayRibbon({ uptime, subsystemMeta }) {
  const win = uptime?.window_30d;
  if (!win) return null;
  const tracked = uptime?.tracked_subsystems || [];
  if (tracked.length === 0) return null;
  return (
    <section className="qt-card mt-10"
             data-testid="status-30d-ribbon"
             style={{ padding: "1.5rem" }}>
      <div className="qt-label" style={{ color: "var(--qt-cyan)",
                                          marginBottom: 16 }}>
        TRACK RECORD · LAST 30 DAYS
      </div>
      <div className="grid grid-cols-1 gap-4">
        {tracked.map((key) => {
          const rows = win.subsystems[key] || [];
          if (rows.length === 0) return null;
          const okCount = rows.filter((r) => r.status === "ok").length;
          const knownCount = rows.filter((r) => r.status !== "unknown").length;
          const pct = knownCount > 0
            ? (okCount / knownCount) * 100 : null;
          const label = subsystemMeta[key] || key;
          return (
            <div key={key} data-testid={`status-30d-row-${key}`}>
              <div style={{ display: "flex",
                            justifyContent: "space-between",
                            alignItems: "baseline",
                            marginBottom: 6 }}>
                <div style={{ color: "#fff", fontWeight: 600,
                              fontSize: 13 }}>{label}</div>
                <div className="qt-mono"
                     style={{ color: "var(--qt-text-3)", fontSize: 11 }}>
                  {pct !== null ? `${pct.toFixed(2)}% OK` : "—"}
                </div>
              </div>
              <UptimeStrip
                buckets={rows}
                height={10}
                gap={1}
                testId={`status-30d-strip-${key}`}
              />
              <div className="qt-mono"
                   style={{ color: "var(--qt-text-3)", fontSize: 9,
                            letterSpacing: "0.08em", marginTop: 4,
                            opacity: 0.55, display: "flex",
                            justifyContent: "space-between" }}>
                <span>30 DAYS AGO</span>
                <span>NOW →</span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}


function SubscribeRow() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | busy | success | error
  const [msg,   setMsg]   = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) return;
    setState("busy"); setMsg("");
    try {
      const r = await axios.post(`${API}/public/status/subscribe`,
                                  { email });
      setState("success");
      setMsg(r.data?.already_subscribed
        ? "You're already on the list — thanks!"
        : "Subscribed. We'll email you when incidents start or end.");
    } catch (e) {
      setState("error");
      setMsg(e?.response?.data?.detail || "Subscribe failed. Try again?");
    }
  };

  return (
    <div data-testid="status-subscribe-section"
         style={{ marginTop: 32, padding: "18px 20px", borderRadius: 6,
                  background: "rgba(4,9,20,0.55)",
                  border: "1px solid rgba(255,255,255,0.08)" }}>
      <div style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "flex-start", gap: 18, flexWrap: "wrap" }}>
        <div style={{ minWidth: 280, flex: 1 }}>
          <div className="qt-label"
               style={{ letterSpacing: "0.18em",
                        display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Mail size={11} /> SUBSCRIBE TO STATUS UPDATES
          </div>
          <p style={{ color: "var(--qt-text-3)", fontSize: 12,
                      marginTop: 8, lineHeight: 1.5 }}>
            Get an email when we open or resolve an incident. Low volume —
            no marketing.
          </p>
          <form onSubmit={submit}
                data-testid="status-subscribe-form"
                style={{ marginTop: 10, display: "flex", gap: 8,
                         flexWrap: "wrap" }}>
            <input type="email"
                   required
                   value={email}
                   onChange={(e) => setEmail(e.target.value)}
                   placeholder="you@company.com"
                   data-testid="status-subscribe-email"
                   className="qt-input"
                   style={{ flex: 1, minWidth: 220, fontSize: 13 }} />
            <button type="submit"
                    disabled={state === "busy"}
                    data-testid="status-subscribe-submit"
                    className="qt-btn qt-btn-primary"
                    style={{ fontSize: 12 }}>
              {state === "busy"
                ? <Loader2 size={12} className="animate-spin" />
                : <Mail size={12} />}
              {state === "busy" ? "Subscribing…" : "Subscribe"}
            </button>
          </form>
          {msg && (
            <div data-testid="status-subscribe-msg"
                 style={{
                   marginTop: 8, fontSize: 12,
                   color: state === "success" ? "#1FDD83" : "#FF6E6E",
                 }}>
              {msg}
            </div>
          )}
        </div>
        <a href={`${API}/public/status/feed.rss`}
           target="_blank" rel="noopener noreferrer"
           data-testid="status-rss-link"
           style={{ color: "#FF9100",
                    display: "inline-flex", alignItems: "center",
                    gap: 6, fontSize: 13, textDecoration: "none",
                    border: "1px solid #FF910055",
                    padding: "8px 12px", borderRadius: 4,
                    whiteSpace: "nowrap" }}>
          <Rss size={12} /> RSS feed
        </a>
      </div>
    </div>
  );
}
